import { spanishPhonotactics } from "../phonotactics/spanishPhonotactics"
import { SegmentStrategy } from "../rules/intoSegments"
import { Rule } from "../rules/rule"
import { RuleSet } from "../rules/ruleSet"

export type SpanishAccentType =
  | 'Mexico'
  | 'Spain'
  | 'Americanized'
  ;
export const supportedSpanishAccents = [
  'Mexico',
  'Spain',
  'Americanized',
];

const vowelOrR = { or: ['/a/', '/e/', '/i/', '/o/', '/u/', '/r/', '/ɾ/'] }

/** pronounce Spanish */
export const spanishToIpa: RuleSet = {
  preprocess: ['lowerCase', 'cleanPunctuation'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.Ipa,
  phonemeLengths: { map: { a: 160, e: 160, i: 160, o: 160, u: 160 } },
  rules: [
    { from: 'á', to: 'ˈa' },
    { from: 'é', to: 'ˈe' },
    { from: 'í', to: 'ˈi' },
    { from: 'ó', to: 'ˈo' },
    { from: 'ú', to: 'ˈu' },
  ],
  rules2: [
    // words
    { from: 'mˈexico', to: 'ˈmexiko', previous: ['#word'], next: ['#word'] },
    { from: 'oaxaca', to: 'waˈxaka', previous: ['#word'], next: ['#word'] },
    // small words to de-emphasize
    { from: 'de', to: 'deˑ', previous: ['#word'], next: ['#word'] },
    { from: 'el', to: 'eˑl', previous: ['#word'], next: ['#word'] },
    { from: 'es', to: 'eˑs', previous: ['#word'], next: ['#word'] },
    { from: 'la', to: 'laˑ', previous: ['#word'], next: ['#word'] },

    // consonants
    { from: 'b', to: 'b', previous: ['#word'] },
    { from: 'b', to: 'b', previous: { or: ['/m/', '/n/'] } },
    { from: 'b', to: 'β' },
    { from: 'ch', to: 't͡ʃ' },
    { from: 'cci', to: 'kθj', next: ['vowel'] },
    { from: 'cci', to: 'kθi', next: ['consonant'] },
    { from: 'c', to: 'θ', next: { or: ['/e/', '/i/'] } },
    { from: 'c', to: 'k' },
    { from: 'd', to: 'ð', previous: vowelOrR, next: { or: [':vowel', '#word'] } },
    { from: 'd', to: 'd' },
    { from: 'f', to: 'f' },
    { from: 'gü', to: 'gw' },
    { from: 'gu', to: 'gu', next: ['consonant'] },
    { from: 'gu', to: 'ɣ', previous: vowelOrR, next: { or: ['/e/', '/i/'] } },
    { from: 'gu', to: 'ɣw', previous: vowelOrR, next: ['vowel'] },
    { from: 'gu', to: 'g' },
    { from: 'g', to: 'x', next: { or: ['/e/', '/i/'] } },
    { from: 'g', to: 'ɣ', previous: vowelOrR, next: { or: [':vowel', '#word'] } },
    { from: 'g', to: 'g' },
    { from: 'j', to: 'x' },
    { from: 'll', to: 'ʎ' },
    { from: 'l', to: 'l' },
    { from: 'm', to: 'm' },
    { from: 'ñ', to: 'ɲ' }, { from: 'n\u0303', to: 'ɲ' },
    { from: 'n', to: 'n', next: { seq: ['/g/', { or: ['/e/', '/i/'] }] } },
    { from: 'n', to: 'ŋ', next: 'g' },
    { from: 'n', to: 'n' },
    { from: 'p', to: 'β', next: 't' },
    { from: 'p', to: 'p' },
    { from: 'qü', to: 'kw' },
    { from: 'qu', to: 'k' },
    { from: 'rr', to: 'r' },
    { from: 'r', to: 'r', previous: ['#word'] },
    { from: 'r', to: 'ɾ' },
    { from: 's', to: 'z', next: ['voiced'] },
    { from: 's', to: 's' },
    { from: 'tz', to: 'ts' },
    { from: 't', to: 't' },
    { from: 'v', to: 'b', previous: ['#word'] },
    { from: 'v', to: 'b', previous: { or: ['/m/', '/n/'] } },
    { from: 'v', to: 'β' },
    { from: 'y', to: 'i', previous: ['#word'], next: ['#word'] },
    { from: 'y', to: 'j', next: ['#word'] },
    { from: 'y', to: 'ʝ' },
    { from: 'x', to: 's', previous: ['#word'] },
    { from: 'x', to: 'ks' },
    { from: 'z', to: 'ð̬', next: ['voiced'] }, // z or ð depending on accent
    { from: 'z', to: 'θ' },

    // diphthongs (but not if second is stressed, e.g., <aó>)
    { from: 'aˈi', to: 'aˈi' }, { from: 'ai', to: 'ai̯' },
    { from: 'aˈo', to: 'aˈo' }, { from: 'ao', to: 'ao̯' },
    { from: 'aˈu', to: 'aˈu' }, { from: 'au', to: 'au̯' },
    { from: 'eˈi', to: 'eˈi' }, { from: 'ei', to: 'ei̯' },
    { from: 'eˈu', to: 'eˈu' }, { from: 'eu', to: 'eu̯' },
    { from: 'oˈi', to: 'oˈi' }, { from: 'oi', to: 'oi̯' },
    { from: 'oˈu', to: 'oˈu' }, { from: 'ou', to: 'ou̯' },

    // vowels & semi-vowels
    { from: 'a', to: 'a' },
    { from: 'e', to: 'e' },
    { from: 'i', to: 'j', previous: ['consonant'], next: ['vowel'] }, // i > j / C_V
    { from: 'i', to: 'i' },
    { from: 'o', to: 'o' },
    { from: 'u', to: 'w', previous: ['consonant'], next: ['vowel'] }, // u > w / C_V
    { from: 'u', to: 'u' },

    // do this after diphthongs, since <h> is basically a separator
    { from: 'h', to: '' },
  ],

  stressRule: {
    default: -1,
    toCheck: [{
      which: 0,
      pattern: { seq: ['#word', { or: ['/deˑ/', '/eˑs/', '/eˑl/', '/laˑ/'] }, '#word'] }
    }, {  // ends in diphthong, accent on 2nd to last vowel, which is considered the final syllable
      which: -1,
      pattern: { seq: [{ and: [':vowel', ':non-syllabic'] }, '#word'] }
    }, {
      which: -2,
      pattern: { seq: [{ or: [':vowel', '/n/', '/s/'] }, '#word'] }
    }]
  },
  phonotactics: spanishPhonotactics,
}

/*
in some accents:
  c > θ / _{e|i}
  z > ð / _{+voiced}
  z > θ

and others:
  c > s / _{e|i}
  z > z / _{+voiced}
  z > s

For [θ|s], the decision can easily be made in follow-up rules
  by having the initial rule output θ.
But [ð|z] is trickier because sometimes d>ð and sometimes s>z.
  In order to defer the decision, the rule uses /ð̬/
  "voiced ð", which is a vague [ð] or [z].
*/

/** general-purpose detailed pronunciation rules */
const detailedRules: Rule[] = [
  { from: 't', to: 't̪' },

  { from: 'e', to: 'e̞' },
  { from: 'o', to: 'o̞' },
];

/** Spanish accent that works with the output of spanishToIpa */
export const spanishAccent: RuleSet = {
  phonemeLengths: { map: { ɶ: 160, e̞: 160, i: 160, o̞: 160, u: 160, ɐ: 150 } },
  rules: [
    ...detailedRules,
    { from: 'a', to: 'ɐ', next: ['#word'] },
    { from: 'a', to: 'ɶ' },
    { from: 'ʝ', to: 'j' },
    { from: 'ʎ', to: 'j' },
    { from: 'ð̬', to: 'ð' },
  ]
}

/** Mexican accent that works with the output of spanishToIpa */
export const mexicanAccent: RuleSet = {
  phonemeLengths: { map: { ɶ: 160, e̞: 160, i: 160, o̞: 160, u: 160, ɐ: 150 } },
  rules: [
    ...detailedRules,
    { from: 'a', to: 'ɐ', next: ['#word'] },
    { from: 'a', to: 'ɶ' },
    { from: 'ʎ', to: 'ʝ' },
    { from: 'θ', to: 's' },
    { from: 'ð̬', to: 'z' },
  ]
}
